import { useContext } from 'react';
import { FormProvider } from 'react-hook-form';
import { GovIcon, GovMessage } from '@gov-design-system-ce/react';
import { UiBlockingGroupContext } from '@gov-nx/component/common';
import { MarkdownRender } from '@gov-nx/component/web';
import { useExtraConfigContext } from '@gov-nx/core/app';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import { DalsiNastaveniDatovkyContext, PageCode } from '@gov-nx/module/page';
import { Button, FormSwitch, LayoutGap } from '@gov-nx/ui/web';
import { DataboxAdditionalSettingsSkeleton } from './DataboxAdditionalSettingsSkeleton';
import { DataboxDisconnectModal } from './DataboxDisconnectModal';

export const DataboxAdditionalSettings = () => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tds = getLocalizeCurried(PageCode['datove-schranky']);

	const context = useContext(DalsiNastaveniDatovkyContext);
	const uiBlocking = useContext(UiBlockingGroupContext);

	const {
		extraConfig: {
			pageRoutes: {
				databoxConfig: { displayAutoArchiveToggle },
			},
		},
	} = useExtraConfigContext();

	if (!context) {
		return null;
	}

	const {
		databoxId,
		databox,
		controls,
		formDefinitions,
		isAutomaticArchiveEnabled,
		openDisconnectConfirmation,
	} = context;

	if (controls.initialLoading) {
		return <DataboxAdditionalSettingsSkeleton />;
	}

	return (
		<>
			{displayAutoArchiveToggle &&
				(databox?.typSchranky === 'PO' || databox?.typSchranky === 'PO_REQ' ? (
					<GovMessage
						color="warning"
						type="subtle"
						icon={
							<GovIcon
								type="components"
								name="info-circle-fill"
							/>
						}>
						<p>{tds('automaticka-archivace.nedostupna-PO')}</p>
					</GovMessage>
				) : (
					<FormProvider {...formDefinitions.formMethods}>
						<div>
							<FormSwitch
								field={{
									identifier: `${databoxId}-shouldArchiveAutomatically`,
									name: `${databoxId}.shouldArchiveAutomatically`,
									isDisabled:
										controls.initialLoading ||
										controls.processLoading ||
										uiBlocking?.isBlockedEditation,
									label: {
										children: tds('automaticka-archivace.label'),
										identifier: `${databoxId}-shouldArchiveAutomatically`,
									},
									'aria-describedby': `automaticka-archivace-plny-popis_${databoxId}`,
								}}
							/>
							<LayoutGap gap="s">
								<p
									className="text-body-s! text-(--text-secondary) ml-12"
									id={`automaticka-archivace-plny-popis_${databoxId}`}>
									{tds('automaticka-archivace.plny-popis')}
								</p>
								<MarkdownRender
									applyClasses={{
										paragraph: 'text-xs md:text-s text-secondary-700',
									}}>
									{tds('automaticka-archivace.varovani.fikce')}
								</MarkdownRender>
							</LayoutGap>
						</div>
						{isAutomaticArchiveEnabled ? (
							<div>
								<FormSwitch
									field={{
										name: `${databoxId}.shouldArchiveLargeMessagesAutomatically`,
										identifier: `${databoxId}-shouldArchiveLargeMessagesAutomatically`,
										isDisabled:
											controls.initialLoading ||
											controls.processLoading ||
											uiBlocking?.isBlockedEditation,
										label: {
											children: tds('automaticka-archivace-velke-zpravy.label'),
											identifier: `${databoxId}-shouldArchiveLargeMessagesAutomatically`,
										},
										'aria-describedby': `automaticka-archivace-plny-popis_${databoxId}`,
									}}
								/>
								<p
									className="text-body-xs md:text-body-s text-(--text-secondary)"
									id={`automaticka-archivace-plny-popis_${databoxId}`}>
									{tds('automaticka-archivace-velke-zpravy.plny-popis')}
								</p>
							</div>
						) : null}
					</FormProvider>
				))}

			<div>
				<Button
					color="error"
					type="outlined"
					isDisabled={controls.initialLoading || controls.processLoading}
					onClick={openDisconnectConfirmation}
					aria-label={tds('odpojeni.odpojit-datovku-z-portalu')}
					className="ml-4">
					{tds('odpojeni.odpojit-datovku-z-portalu')}
				</Button>
				<DataboxDisconnectModal />
			</div>
		</>
	);
};
